import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import classNames from 'classnames';

import { FetchQuestionComments } from "../../../../../connection/questions";
import { CreateLiveChatOrQuestionMessage } from "../../../../../connection/save-chat-message";
import { toggleRegistrationModal } from "../../../../../store/actions/event/event-actions";
import { setModuleQuestionsComments } from "../../../../../store/actions/event/questions-actions";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { PageModule, IQuestion, ISocketCommentProps, SqsSocketTypes, Status } from "../../../../../types/working-model";
import { getStorageItem } from "../../../../../utils/local-storage";
import Icon, { COLORS, ICONS } from "../../../../general-ui/icon";
import WaitingIndicator from "../../../../general-ui/waiting-indicator/waiting-indicator";
import { ParamsProps } from "../../../live-event";
import AddQuestionComment from "./add-question-comment";
import QuestionComments from "./question-comments";
import ShowMoreQuestionComments from "./show-more-question-comments";
import TimeSince from "./time-since";

const BATCH_QUANTITY = 50;

interface IQuestionCardProps extends React.ComponentPropsWithoutRef<'div'> {
	question: IQuestion;
	module: PageModule;
	inView: boolean;
	session: number;
	session_uuid: string;
}

const QuestionCard = React.forwardRef<HTMLDivElement, IQuestionCardProps>(({
	question,
	module,
	inView,
	session,
	session_uuid,
}, ref) => {
	const [commentsQuantityToShow, setCommentsQuantityToShow] = useState(3);
	const [initialized, setInitialized] = useState(false);
	const [fetching, setFetching] = useState(false);
	const [hasMore, setHasMore] = useState(false);
	const [hasFocus, setHasFocus] = useState(false);
	const [focusCommentInput, setFocusCommentInput] = useState(false);

	const reduxComments = useTypedSelector(state => state.QuestionsReducer.comments?.[module?.id || 0]?.[question?.id]);

	const comments = useMemo(() => {
		return reduxComments || new Map();
	}, [reduxComments]);

	const dispatch = useDispatch();

	const { t } = useTranslation("session");
	const { language } = useParams<ParamsProps>();

	useEffect(() => {
		// fetch the newest batch of BATCH_QUANTITY comments for this question
		const fetchInitialBatch = async () => {
			try {
				if (!module.id) return;
				setFetching(true);
				const token = getStorageItem('Bl-Profile-Token') ?? '';
				const result: {
					comments: ISocketCommentProps[],
					has_more: boolean,
					total_comments: number,
					responseCode?: number,
					msg?: string;
				} = await FetchQuestionComments(question.id, token);
				if (result?.responseCode === 400) {
					console.error(result);
					setFetching(false);
					return;
				}
				if (!result?.comments) {
					return setFetching(false);
				}
				const commentsMap = new Map<number, ISocketCommentProps>();
				result?.comments?.forEach(comment => {
					commentsMap.set(comment.id, comment);
				});
				dispatch(setModuleQuestionsComments(module.id, question.id, commentsMap));
				setHasMore(result.has_more);
				setFetching(false);
			} catch (e) {
				console.error(e);
				setFetching(false);
			}
		};

		if (inView && !initialized) {
			setInitialized(true);
			fetchInitialBatch();
		}

	}, [question, inView, initialized, dispatch, module.id]);

	const handleShowMore = async (quantity: number) => {
		if (!module.id) return;
		setCommentsQuantityToShow(quantity);
		if (quantity > BATCH_QUANTITY && hasMore) {
			// fetch next BATCH_QUANTITY comments
			try {
				setFetching(true);
				const token = getStorageItem('Bl-Profile-Token') ?? '';
				const result: {
					comments: ISocketCommentProps[],
					has_more: boolean,
					responseCode?: number,
					total_comments: number,
				} = await FetchQuestionComments(question.id, token, Math.floor(quantity / BATCH_QUANTITY));
				if (result?.responseCode === 400) {
					console.error(result);
					setFetching(false);
					return;
				}
				const commentsCopy = new Map(comments);
				result?.comments?.forEach(comment => {
					commentsCopy.set(comment.id, comment);
				});
				setHasMore(!!result?.has_more);
				dispatch(setModuleQuestionsComments(module.id, question.id, commentsCopy));
				setFetching(false);
			} catch (e) {
				console.error(e);
				setFetching(false);
			}
		}
	};

	const submitLike = async () => {
		const token = getStorageItem('Bl-Profile-Token');
		if (!token) {
			dispatch(toggleRegistrationModal(session_uuid));
			return;
		}
		try {
			await CreateLiveChatOrQuestionMessage(
				session,
				{
					question_id: question.id,
					module_id: module.id,
					message_type: SqsSocketTypes.QUESTION_LIKE,
					session_uuid,
					language,
				},
				token,
				SqsSocketTypes.QUESTION_LIKE,
			);
		} catch (e) {
			console.error(e);
		}
	};

	const submitUnLike = async () => {
		const token = getStorageItem('Bl-Profile-Token');
		if (!token) {
			dispatch(toggleRegistrationModal(session_uuid));
			return;
		}
		try {
			await CreateLiveChatOrQuestionMessage(
				session,
				{
					question_id: question.id,
					module_id: module.id,
					message_type: SqsSocketTypes.QUESTION_UNLIKE,
					session_uuid,
					language,
				},
				token,
				SqsSocketTypes.QUESTION_UNLIKE,
			);
		} catch (e) {
			console.error(e);
		}
	};

	const viewableComments = useMemo(() => {
		const filtered = Array.from(comments).filter(data => data[1].status === Status.Approved && !data[1].spam);
		return filtered.slice(0, commentsQuantityToShow);
	}, [commentsQuantityToShow, comments]);

	const onFocus = useCallback(() => setHasFocus(true), []);
	const onBlur = useCallback(() => setHasFocus(false), []);

	const handleCommentIconClick = () => {
		// passing props back and forth that deep
		setFocusCommentInput(true);
	};

	// we actually wnat to allow spam messages through because we want to display the message to the original sender
	// it will not be sent to anyone else because the socket won't trigger
	if (question.status !== Status.Approved && !question.spam) return null;
	return (
		<div ref={ref} className={classNames("question-card", { focused: hasFocus })}>
			<div className="question-card-questionee">
				{question.first_name} {question.last_name} &bull; <TimeSince createdAt={question.created_at} />
			</div>
			<h4 className="question-card-question">{question.question}</h4>
			<div className="question-card-statistics">
				<div>{question?.likes ?? 0} <span>{t("Likes")}</span></div>
				<div>{question?.total_comments ?? 0} <span>{t("Comments")}</span></div>
			</div>
			<div className="question-card-divider" />
			<div className="question-card-actions">
				{question.has_liked ? (
					<button className="clear no-style" onClick={submitUnLike}><Icon name={ICONS.LIKE} color={COLORS.DEFAULT} size={16} /> <span>{t("Un-like")}</span></button>
				) : (
					<button className="clear no-style" onClick={submitLike}><Icon name={ICONS.LIKE} color={COLORS.DEFAULT} size={16} /> <span>{t("Like")}</span></button>
				)}
				<button onClick={handleCommentIconClick} className="clear no-style"><Icon name={ICONS.COMMENT_EMPTY} color={COLORS.DEFAULT} size={16} /> <span>{t("Comment")}</span></button>
			</div>
			<div className="question-card-divider" />
			<AddQuestionComment
				focusCommentInput={focusCommentInput}
				setFocusCommentInput={setFocusCommentInput}
				module={module}
				question={question}
				sessionUuid={session_uuid}
				onBlur={onBlur}
				onFocus={onFocus}
			/>
			{fetching ? <div className="comments-loading-container"><WaitingIndicator /></div> : null}
			<QuestionComments comments={viewableComments} />
			{comments?.size ? <ShowMoreQuestionComments total_comments={question?.total_comments ?? 0} onClick={handleShowMore} /> : null}
		</div>
	);
});

QuestionCard.displayName = 'QuestionCard';

export default QuestionCard;
